import { useCallback, useMemo } from 'react';

export interface MentionUserRequest {
  userId: number;
  userName: string;
  userDetailId: number;
}

export interface HashTagRequest {
  hashtag: string;
  entityType: string;
  entityId?: number;
}

interface UseMentionsProps {
  projectUsers: Array<{
    label: string;
    value: number;
    roleName?: string;
    userDetailId: number;
  }>;
  taskDropdownData: Array<{
    label: string;
    value: number;
    entityType: string;
  }>;
}

export const useMentions = ({ projectUsers, taskDropdownData }: UseMentionsProps) => {
  // Function to replace @UserName with @userId in message text
  const processMessageForSocket = useCallback((message: string, mentions: MentionUserRequest[]) => {
    let processedMessage = message;
    
    mentions.forEach((mention) => {
      const userName = mention.userName;
      const userId = mention.userId;
      
      // Replace @UserName with @userId in the message
      const userNameRegex = new RegExp(`@${userName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, 'g');
      processedMessage = processedMessage.replace(userNameRegex, `@${userId}`);
    });
    
    return processedMessage;
  }, []);

  // Function to replace @userId with @UserName for display when receiving from socket
  const processMessageForDisplay = useCallback((message: string, mentions: MentionUserRequest[]) => {
    let processedMessage = message;
    
    mentions.forEach((mention) => {
      const userName = mention.userName;
      const userId = mention.userId;
      
      // Replace @userId with @UserName for display
      const userIdRegex = new RegExp(`@${userId}`, 'g');
      processedMessage = processedMessage.replace(userIdRegex, `@${userName}`);
    });
    
    return processedMessage;
  }, []);

  // Parse message to extract actual mentions from text
  const parseMentionsFromText = useCallback((text: string): MentionUserRequest[] => {
    const mentionRegex = /@(\w+)/g;
    const foundMentions: MentionUserRequest[] = [];
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      const mentionText = match[1];
      
      // Try to find user by name first
      const userByName = projectUsers.find(user => 
        user.label.toLowerCase() === mentionText.toLowerCase()
      );
      
      if (userByName) {
        // Check if already added to prevent duplicates
        const alreadyExists = foundMentions.some(m => m.userId === userByName.value);
        if (!alreadyExists) {
          foundMentions.push({
            userId: userByName.value,
            userName: userByName.label,
            userDetailId: userByName.userDetailId,
          });
        }
      } else {
        // Try to find by ID if it's a number
        const userId = parseInt(mentionText);
        if (!isNaN(userId)) {
          const userById = projectUsers.find(user => user.value === userId);
          if (userById) {
            const alreadyExists = foundMentions.some(m => m.userId === userId);
            if (!alreadyExists) {
              foundMentions.push({
                userId: userById.value,
                userName: userById.label,
                userDetailId: userById.userDetailId,
              });
            }
          }
        }
      }
    }

    return foundMentions;
  }, [projectUsers]);

  // Parse hashtags from text
  const parseHashtagsFromText = useCallback((text: string): HashTagRequest[] => {
    const hashtagRegex = /#(\w+)/g;
    const foundHashtags: HashTagRequest[] = [];
    let match;

    while ((match = hashtagRegex.exec(text)) !== null) {
      const hashtagText = match[1];
      
      // Try to find task by name
      const taskByName = taskDropdownData.find(task => 
        task.label.toLowerCase() === hashtagText.toLowerCase()
      );
      
      if (taskByName) {
        const alreadyExists = foundHashtags.some(h => h.entityId === taskByName.value);
        if (!alreadyExists) {
          foundHashtags.push({
            hashtag: taskByName.label,
            entityType: taskByName.entityType || "Task",
            entityId: taskByName.value,
          });
        }
      }
    }

    return foundHashtags;
  }, [taskDropdownData]);

  // Synchronize mentions and hashtags with text content
  const synchronizeWithText = useCallback((
    text: string,
    currentMentions: MentionUserRequest[],
    currentHashtags: HashTagRequest[]
  ) => {
    const textMentions = parseMentionsFromText(text);
    const textHashtags = parseHashtagsFromText(text);

    // Keep only mentions that are still in the text
    const validMentions = currentMentions.filter(mention => {
      const userNameExists = text.includes(`@${mention.userName}`);
      const userIdExists = text.includes(`@${mention.userId}`);
      return userNameExists || userIdExists;
    });

    // Add new mentions found in text
    textMentions.forEach(mention => {
      const exists = validMentions.some(m => m.userId === mention.userId);
      if (!exists) {
        validMentions.push(mention);
      }
    });

    // Keep only hashtags that are still in the text
    const validHashtags = currentHashtags.filter(hashtag => {
      return text.includes(`#${hashtag.hashtag}`);
    });

    // Add new hashtags found in text
    textHashtags.forEach(hashtag => {
      const exists = validHashtags.some(h => h.entityId === hashtag.entityId);
      if (!exists) {
        validHashtags.push(hashtag);
      }
    });

    return {
      mentions: validMentions,
      hashtags: validHashtags,
    };
  }, [parseMentionsFromText, parseHashtagsFromText]);

  // Add mention without duplicates
  const addMention = useCallback((
    currentMentions: MentionUserRequest[],
    newMention: MentionUserRequest
  ): MentionUserRequest[] => {
    const existingMention = currentMentions.find(m => m.userId === newMention.userId);
    if (existingMention) {
      console.log(`Duplicate mention prevented for user: ${newMention.userName} (ID: ${newMention.userId})`);
      return currentMentions;
    }
    return [...currentMentions, newMention];
  }, []);

  // Add hashtag without duplicates
  const addHashtag = useCallback((
    currentHashtags: HashTagRequest[],
    newHashtag: HashTagRequest
  ): HashTagRequest[] => {
    const existingHashtag = currentHashtags.find(h => h.entityId === newHashtag.entityId);
    if (existingHashtag) {
      console.log(`Duplicate hashtag prevented for task: ${newHashtag.hashtag} (ID: ${newHashtag.entityId})`);
      return currentHashtags;
    }
    return [...currentHashtags, newHashtag];
  }, []);

  // Filter dropdown items
  const getFilteredMentions = useCallback((searchTerm: string) => {
    return projectUsers.filter(user =>
      searchTerm === "" ||
      user.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [projectUsers]);

  const getFilteredTasks = useCallback((searchTerm: string) => {
    return taskDropdownData.filter(task =>
      searchTerm === "" ||
      task.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [taskDropdownData]);

  return {
    processMessageForSocket,
    processMessageForDisplay,
    parseMentionsFromText,
    parseHashtagsFromText,
    synchronizeWithText,
    addMention,
    addHashtag,
    getFilteredMentions,
    getFilteredTasks,
  };
};